<template>
    <form class="cycle-overview-form" action="" @submit.stop.prevent="">
        <div class="title">
            <span>{{ $translate('cycle_overview') }}</span>
        </div>

        <div class="cycle-info">
            <div class="fields">
                <div class="input-container">
                    <label for="inseminated"> {{ $translate('total_inseminated_eggs') }} </label>
                    <input
                        id="inseminated"
                        v-model="inseminated"
                        name="inseminated"
                        type="number"
                        placeholder="-"
                        min="0"
                        max="50"
                    />
                    <label for="inseminated" class="icon"><i class="fa fa-pencil"></i></label>
                </div>

                <div class="input-container">
                    <label for="fertilized">{{ $translate('number_fertilized') }} (2PN)</label>
                    <input
                        id="fertilized"
                        v-model="fertilized"
                        name="fertilized"
                        type="number"
                        placeholder="-"
                        min="0"
                        max="50"
                    />
                    <label for="fertilized" class="icon"><i class="fa fa-pencil"></i></label>
                </div>

                <div class="input-container">
                    <label for="biopsied">
                        {{ $translate('number_of_biopsied_embryos') }} ({{ $translate('pgt') }})
                    </label>
                    <input
                        id="biopsied"
                        v-model="biopsied"
                        name="biopsied"
                        type="number"
                        placeholder="-"
                        min="0"
                        max="50"
                    />
                    <label for="biopsied" class="icon"><i class="fa fa-pencil"></i></label>
                </div>

                <div class="input-container">
                    <label for="cryopreserved">
                        {{ $translate('number_of_cryopreserved_embryos') }}
                    </label>
                    <input
                        id="cryopreserved"
                        v-model="cryopreserved"
                        name="cryopreserved"
                        type="number"
                        placeholder="-"
                        min="0"
                        max="50"
                    />
                    <label for="cryopreserved" class="icon"><i class="fa fa-pencil"></i></label>
                </div>

                <div class="input-container">
                    <label for="transferred">
                        {{ $translate('number_of_transferred_embryos') }}
                    </label>
                    <input
                        id="transferred"
                        v-model="transferred"
                        name="transferred"
                        type="number"
                        placeholder="-"
                        min="0"
                        max="50"
                    />
                    <label for="transferred" class="icon"><i class="fa fa-pencil"></i></label>
                </div>
            </div>

            <div class="comment-container">
                <textarea
                    id="comment"
                    v-model="comment"
                    name="comment"
                    :placeholder="$translate('write_comment')"
                    maxlength="200"
                />
                <label for="comment" class="icon"><i class="fa fa-pencil"></i></label>
            </div>
        </div>
    </form>
</template>

<script>
import { mapGetters } from 'vuex'
import { WELL_STATUS } from '../../../../business/factory/DataModel'

export default {
    name: 'CycleOverviewForm',
    props: {
        wells: {
            type: Array,
            required: true
        }
    },
    data: () => ({
        inseminated: '',
        fertilized: '',
        biopsied: '',
        cryopreserved: '',
        transferred: '',
        comment: ''
    }),
    computed: {
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly',
            getPnCount: 'timelapse/getPnCount'
        }),
        defaultInseminated() {
            return this.wells.length
        },
        defaultFertilized() {
            if (this.isFdaFriendly) return null

            return this.wells.filter(well => this.getPnCount(well) === 2).length
        },
        defaultCryopreserved() {
            return this.wells.filter(well => well.status === WELL_STATUS.FROZEN).length
        },
        defaultTransferred() {
            return this.wells.filter(well => well.status === WELL_STATUS.TRANSFERRED).length
        }
    },
    mounted() {
        this.setDefaultValues()
    },
    methods: {
        setDefaultValues() {
            this.inseminated = this.defaultInseminated
            this.fertilized = this.defaultFertilized
            this.cryopreserved = this.defaultCryopreserved
            this.transferred = this.defaultTransferred
        }
    }
}
</script>

<style lang="scss" scoped>
$active: $light-3;
$passive: $light;

.cycle-overview-form {
    margin: 20px 0;

    .title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    .cycle-info {
        display: grid;
        grid-template-columns: minmax(min-content, auto) 1fr;
        column-gap: 20px;

        .fields {
            display: grid;
            grid-template-rows: repeat(5, 1fr);

            @media screen {
                row-gap: 10px;
            }
            @media print {
                border: solid 1px $report-color;
            }

            .input-container {
                display: grid;
                height: 40px;
                padding: 5px 10px;

                label {
                    margin-bottom: 0;
                    margin-right: auto;
                    align-self: center;
                    cursor: pointer;

                    &.icon {
                        color: $passive;

                        &:hover {
                            color: $active;
                        }
                    }
                }

                input {
                    background-color: transparent;
                    border: none;
                    align-self: center;
                    color: $active;
                    text-align: center;

                    &::-webkit-input-placeholder {
                        color: $passive;
                    }

                    &:focus ~ label {
                        color: $active;
                    }
                }

                @include input-number-no-arrows();

                @media screen {
                    grid-template-columns: auto 30px 20px;
                    column-gap: 10px;
                    border: solid 1px $passive;
                    border-radius: 4px;
                }

                @media print {
                    grid-template-columns: 2fr 1fr;

                    &:not(:last-child) {
                        border-bottom: solid 1px $report-color;
                    }

                    label,
                    input {
                        color: black;

                        i {
                            display: none;
                        }
                    }
                }
            }
        }

        .comment-container {
            position: relative;

            textarea {
                width: 100%;
                height: 100%;
                padding: 10px 25px 10px 10px;
                background-color: transparent;

                @media screen {
                    border: solid 1px $passive;
                    border-radius: 4px;
                    color: $active;
                }
                @media print {
                    border: solid 1px $report-color;
                    color: black;
                }

                &::-webkit-input-placeholder {
                    @media screen {
                        color: $passive;
                    }
                    @media print {
                        color: transparent;
                    }
                }

                &:focus {
                    border-color: $active;

                    ~ label {
                        display: none;
                    }
                }
            }

            label {
                position: absolute;
                top: 10px;
                right: 10px;
                color: $passive;
                cursor: pointer;

                &:hover {
                    color: $active;
                }

                @media print {
                    display: none;
                }
            }
        }
    }
}
</style>
